import { roundPercentages } from '@/utils/formatters';

function sortTopKeywords(keywords) {
  return keywords.sort((a, b) => b.count - a.count);
}

function normalizeSentiment({ positive, neutral, negative }) {
  const total = positive + neutral + negative;
  const positiveRatio = positive / total;
  const negativeRatio = negative / total;
  const neutralRatio = 1 - (positive + negative) / total;

  return roundPercentages({ positiveRatio, negativeRatio, neutralRatio });
}

const SENTIMENT_CONFIG = {
  POSITIVE: {
    label: 'Positive',
    color: 'var(--color-sentiment-positive)',
    dot: { severity: 'positive' },
  },
  NEUTRAL: {
    label: 'Neutral',
    color: 'var(--color-sentiment-neutral)',
    dot: { severity: 'neutral' },
  },
  NEGATIVE: {
    label: 'Negative',
    color: 'var(--color-sentiment-negative)',
    dot: { severity: 'negative' },
  },
};

export function convertTopKeywordsToSentimentMeters(
  keywords,
  loading = false,
  searchFilterSentiments = [],
) {
  if (loading) {
    return [];
  }

  // If searchFilterSentiments is empty, treat it as if all sentiments are selected.
  const activeSentiments =
    searchFilterSentiments.length > 0
      ? searchFilterSentiments
      : ['POSITIVE', 'NEUTRAL', 'NEGATIVE'];

  const sortedTopKeywords = sortTopKeywords(keywords);

  return sortedTopKeywords.map((keywordData) => {
    const { positive, neutral, negative } = keywordData.sentiment;
    const total = positive + neutral + negative;

    const { positiveRatio, neutralRatio, negativeRatio } = normalizeSentiment(
      keywordData.sentiment,
    );

    const sentimentValueMap = {
      POSITIVE: positive,
      NEUTRAL: neutral,
      NEGATIVE: negative,
    };
    const sentimentRatioMap = {
      POSITIVE: positiveRatio,
      NEUTRAL: neutralRatio,
      NEGATIVE: negativeRatio,
    };

    const data = activeSentiments.map((sentimentKey) => ({
      label: SENTIMENT_CONFIG[sentimentKey].label,
      value: sentimentValueMap[sentimentKey],
      color: SENTIMENT_CONFIG[sentimentKey].color,
    }));

    const tooltipData = [
      {
        label: 'Mentions',
        value: total,
      },
      ...activeSentiments.map((sentimentKey) => ({
        label: SENTIMENT_CONFIG[sentimentKey].label,
        value: sentimentRatioMap[sentimentKey],
        formatType: 'percent',
        formatOptions: {
          style: 'percent',
          maximumFractionDigits: 0,
        },
        dot: SENTIMENT_CONFIG[sentimentKey].dot,
      })),
    ];

    return {
      label: keywordData.keyword,
      data,
      tooltipData,
    };
  });
}

export function filterTooltipMetrics(meterData, meterTooltipData) {
  const hiddenCategories = meterData.reduce(
    (acc, { hidden, label }) => (hidden ? [...acc, label] : acc),
    [],
  );
  return meterTooltipData.filter(({ label }) => !hiddenCategories.includes(label));
}

export const TOPIC_TOP_KEYWORD_SENTIMENT_FILTER = {
  POSITIVE: {
    value: 'positive',
    label: 'Positive',
  },
  NEGATIVE: {
    value: 'negative',
    label: 'Negative',
  },
  NEUTRAL: {
    value: 'neutral',
    label: 'Neutral',
  },
  ALL: {
    value: 'all',
    label: 'All Sentiment',
  },
};

export function convertTopKeywordsToKeywordAndHashtagMeters(
  keywords,
  fieldsToInclude,
  loading = false,
) {
  if (loading) {
    return [];
  }
  const sortedTopKeywords = sortTopKeywords(keywords);
  const filteredKeywords = sortedTopKeywords.filter(({ field }) => fieldsToInclude.includes(field));
  const activeSentiments = ['POSITIVE', 'NEUTRAL', 'NEGATIVE'];

  return filteredKeywords.map((keywordData) => {
    const { field, keyword, count } = keywordData;
    const { positive, neutral, negative } = keywordData.sentiment;

    const { positiveRatio, negativeRatio, neutralRatio } = normalizeSentiment(
      keywordData.sentiment,
    );

    const sentimentValueMap = {
      POSITIVE: positive,
      NEUTRAL: neutral,
      NEGATIVE: negative,
    };

    const data = activeSentiments.map((sentimentKey) => ({
      label: SENTIMENT_CONFIG[sentimentKey].label,
      value: sentimentValueMap[sentimentKey],
      color: SENTIMENT_CONFIG[sentimentKey].color,
    }));

    return {
      field,
      label: keyword,
      data,
      tooltipData: [
        {
          label: 'Mentions',
          value: count,
        },
        {
          label: 'Positive',
          value: positiveRatio,
          formatType: 'percent',
          formatOptions: {
            style: 'percent',
            maximumFractionDigits: 0,
          },
          dot: {
            severity: 'positive',
          },
        },
        {
          label: 'Neutral',
          value: neutralRatio,
          formatType: 'percent',
          formatOptions: {
            style: 'percent',
            maximumFractionDigits: 0,
          },
          dot: {
            severity: 'neutral',
          },
        },
        {
          label: 'Negative',
          value: negativeRatio,
          formatType: 'percent',
          formatOptions: {
            style: 'percent',
            maximumFractionDigits: 0,
          },
          dot: {
            severity: 'negative',
          },
        },
      ],
    };
  });
}
